import * as THREE from 'three';
import { hexDist, hexRing, hexToWorld, type Hex, type MapDef } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { WORLD_PER_HEX } from '../engine/coords.ts';
import { Kit, place } from '../seasons/kit.ts';
import { looksRng, SkyLook, TAU, type OmenCtx, type OmenScene } from './common.ts';

/**
 * The tip of the titan's tail (El Cuerno, docs/SAGAS.md): the most open place in the game. A big pale sky, wind
 * combing the grass, the tail's dorsal plates standing up in a slate ridge across the field, and past the edge of
 * the grass a rock cliff dropping to the sea far below, whitecaps drifting and gulls wheeling over it. The clan of
 * the Horn's marks: cairns, standing stones daubed red, a horn hung on a post. At `pen` (the sim's), the corral the
 * herd is driven to: a ring of weathered rails, its gate open toward the field, hay and a trough inside.
 *
 * Cheap like the woods: every static piece is one merged mesh (vertex colours); the grass one instanced mesh swayed
 * in the vertex shader; the sea one flat ring; whitecaps, seeds on the wind and the light one glow layer; the gulls
 * one tiny instanced mesh. The outskirts' trees are hidden while it stands (the tail has none). No lights.
 *
 * `grave` (El Cuerno 4): the clan's graveyard on the tail, at night. The grass gone pale and dry, the sea dark, no
 * gulls; grave stones carved with the horn on low mounds, long cairns, the bigger family graves kerbed in stone (one
 * of them Ragna and Ulla's mother's, near the camp, red wool on its stone and pale flowers left on it), two great horn
 * stones where a road comes in, clan poles with the horn and red wool. Mist low among the graves, cold moonlight, a
 * few pale wisps drifting over the stones.
 */

const SLATE = ['#646a72', '#6e737a', '#5a6068', '#767b80'];
const ROCK = ['#6a6e72', '#5e6266', '#74787a', '#55595e'];
const CAIRN = ['#6e7378', '#62676c', '#7a7f84'];
const RAIL = ['#8a7a68', '#7e705f', '#93836f'];
const POST = ['#5e4c3c', '#54443a'];
const HAY = ['#8e9064', '#868a5e', '#94966c'];
const CLAN = '#a43a2c';
const SEA_NEAR = new THREE.Color('#2c5c76');
const SEA_FAR = new THREE.Color('#7ea7c2');
const SEA_NEAR_N = new THREE.Color('#0c1c2a');
const SEA_FAR_N = new THREE.Color('#203852');
const CLIFF = ['#5c6066', '#676a6e', '#545a60', '#71746f'];
const HIDE = ['#7a6450', '#6e5a48', '#836c56'];
const EMBER = new THREE.Color('#ff9a5a');
/** The sea's height under the cliff (a falling nightmare drops this far). */
export const TAIL_SEA_Y = -3.4;

/** The clan's summer camp on the lash (El Cuerno 2): its mooring posts and barrels, and the way the tail swings. */
export interface TailCamp {
  posts: { x: number; y: number }[];
  barrels: { x: number; y: number }[];
  /** The swing's axis (sim angle steps, 0..1023). */
  axis: number;
}
const FOAM = new THREE.Color('#eef4f8');
const SEED = new THREE.Color('#f2f6ee');
const SUN = new THREE.Color('#f4f8ff');
const GRAVE = ['#8e9298', '#9a9ea3', '#84888e', '#a3a6aa'];
const GRAVE_D = ['#4a4e55', '#43474e'];
const MOUND = ['#4a4438', '#423d33', '#504a3d'];
const BLOOM = ['#e6e8ec', '#d8dce6', '#eef0f4'];
const MIST = new THREE.Color('#8ea0c4');
const MOON = new THREE.Color('#c8d6f4');
const WISP = new THREE.Color('#b8d4ff');
const BLOOM_GLOW = new THREE.Color('#e8eeff');

/** How the tail looks: the clan's graveyard at night (El Cuerno 4). */
export interface TailLook {
  grave?: boolean;
}

const P = (() => {
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
  const box = new THREE.BoxGeometry(1, 1, 1);
  const rock = new THREE.DodecahedronGeometry(0.5, 0);
  const slab = new THREE.CylinderGeometry(0.5, 0.5, 1, 7);
  const disc = new THREE.CylinderGeometry(0.5, 0.5, 0.02, 20);
  // A floor of trodden earth: a thick round slab, sunk so the uneven turf never shows through it.
  const pad = new THREE.CylinderGeometry(0.5, 0.5, 1, 28).translate(0, -0.5, 0);
  const cone = new THREE.ConeGeometry(0.5, 1, 5);
  const blob = new THREE.IcosahedronGeometry(0.5, 0);
  const coil = new THREE.TorusGeometry(0.5, 0.1, 5, 16).rotateX(Math.PI / 2);
  // A horn carved in relief: a curved half-ring standing on edge.
  const horn = new THREE.TorusGeometry(0.5, 0.11, 4, 10, Math.PI * 0.85);
  return { cyl, box, rock, slab, disc, pad, cone, blob, coil, horn };
})();

const pick = <T>(list: T[], r: number): T => list[Math.floor(r * list.length) % list.length] as T;

/** Grass that bends with the wind: the blade's tip swings along the wind, more the higher up it is. */
const swayGrass = (mat: THREE.MeshStandardMaterial, time: { value: number }): void => {
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uTime = time;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vec4 wp = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        float gust = 0.55 + 0.45 * sin(uTime * 0.7 + wp.x * 0.18 + wp.z * 0.11);
        float k = max(position.y, 0.0) * (0.35 + 0.25 * sin(uTime * 2.3 + wp.x * 1.3 + wp.z * 0.9)) * gust;
        transformed.x += k * 0.9;
        transformed.z += k * 0.35;`,
      );
  };
  mat.customProgramCacheKey = () => 'tail-grass';
};

export const buildTail = (ctx: OmenCtx, map: MapDef, herd: { start: { x: number; y: number }; pen: { x: number; y: number }; penR: number } | null, camp: TailCamp | null = null, look: TailLook = {}): OmenScene => {
  const group = new THREE.Group();
  group.name = 'tail';
  const low = ctx.low;
  const night = !!look.grave;
  const rnd = looksRng(0x7a11 + map.radius * 31);
  const body = new Kit();
  const road: Hex[] = map.sectors.flatMap((s) => s.path);
  const nearRoad = (h: Hex, d = 1): boolean => road.some((r) => hexDist(r, h) <= d);
  const K = WORLD_PER_HEX / 1000;
  const ground = (x: number, z: number): THREE.Vector3 => new THREE.Vector3(x, ctx.ground(x, z), z);
  /** The highest ground under a disc (a floor laid over it sits just above, with no seam). */
  const topOf = (x: number, z: number, r: number): number => {
    let h = -Infinity;
    for (let i = 0; i <= 24; i++) for (const k of [0, 0.5, 1]) h = Math.max(h, ctx.ground(x + Math.cos((i / 24) * TAU) * r * k, z + Math.sin((i / 24) * TAU) * r * k));
    return h;
  };
  const hexAt = (x: number, z: number): Hex => {
    const fr = z / (WORLD_PER_HEX * 0.866);
    const fq = x / WORLD_PER_HEX - fr / 2;
    let q = Math.round(fq);
    let r = Math.round(fr);
    const s = Math.round(-fq - fr);
    const dq = Math.abs(q - fq);
    const dr = Math.abs(r - fr);
    const ds = Math.abs(s + fq + fr);
    if (dq > dr && dq > ds) q = -r - s;
    else if (dr > ds) r = -q - s;
    return { q, r };
  };

  // The tail has no trees: hide the outskirts' woods while this stands (put back on dispose), green the turf a little.
  // And it ends where the field the sim knows ends (a boar never goes past it, a nightmare slid past it falls): the
  // turf is cut off there and the cliff stands at its rim; what grew beyond it (rocks, tufts) is put away meanwhile.
  const out = ctx.scene.getObjectByName('outskirts');
  const hidden: THREE.Object3D[] = [];
  const edge = (map.radius - 1) * 866 * K + 0.35;
  let turf: { mat: THREE.MeshStandardMaterial; was: THREE.Color } | null = null;
  let cut: { geo: THREE.BufferGeometry; was: THREE.BufferAttribute } | null = null;
  let tuckedRoads: { geo: THREE.BufferGeometry; was: THREE.BufferAttribute } | null = null;
  const tucked: { mesh: THREE.InstancedMesh; i: number; m: THREE.Matrix4 }[] = [];
  if (out) {
    // Trees, and the blue crystals of the outskirts (the only self-lit instanced mesh there).
    const crystal = (c: THREE.Object3D): boolean => !!((c as THREE.InstancedMesh).isInstancedMesh && ((c as THREE.Mesh).material as THREE.MeshStandardMaterial).emissive?.getHex());
    for (const c of out.children) {
      if ((c.name === 'trunks' || c.name === 'cones' || c.name === 'blobs' || crystal(c)) && c.visible) {
        c.visible = false;
        hidden.push(c);
      } else if ((c as THREE.InstancedMesh).isInstancedMesh && c.visible) {
        const im = c as THREE.InstancedMesh;
        const m = new THREE.Matrix4();
        const v = new THREE.Vector3();
        const zero = new THREE.Matrix4().makeScale(0, 0, 0);
        for (let i = 0; i < im.count; i++) {
          im.getMatrixAt(i, m);
          v.setFromMatrixPosition(m);
          if (Math.hypot(v.x, v.z) < edge - 0.3) continue;
          tucked.push({ mesh: im, i, m: m.clone() });
          im.setMatrixAt(i, zero);
        }
        im.instanceMatrix.needsUpdate = true;
      }
    }
    const g = out.getObjectByName('outskirts-ground') as THREE.Mesh | undefined;
    if (g) {
      const pos = g.geometry.getAttribute('position');
      const idx = g.geometry.getIndex();
      if (idx) {
        const keep: number[] = [];
        for (let i = 0; i < idx.count; i += 3) {
          const a = idx.getX(i);
          const b = idx.getX(i + 1);
          const c = idx.getX(i + 2);
          const x = (pos.getX(a) + pos.getX(b) + pos.getX(c)) / 3;
          const z = (pos.getZ(a) + pos.getZ(b) + pos.getZ(c)) / 3;
          if (x * x + z * z < edge * edge) keep.push(a, b, c);
        }
        cut = { geo: g.geometry, was: idx };
        g.geometry.setIndex(keep);
      }
      const mat = g.material as THREE.MeshStandardMaterial;
      turf = { mat, was: mat.color.clone() };
      if (night) mat.color.setRGB(0.92, 0.95, 1.0);
      else mat.color.setRGB(1.04, 1.12, 0.98);
    }
    // The roads' ribbon past the rim too.
    const roads = out.getObjectByName('roads') as THREE.Mesh | undefined;
    const ri = roads?.geometry.getIndex();
    if (roads && ri) {
      const pos = roads.geometry.getAttribute('position');
      const keep: number[] = [];
      for (let i = 0; i < ri.count; i += 3) {
        const a = ri.getX(i);
        if (Math.hypot(pos.getX(a), pos.getZ(a)) < edge) keep.push(a, ri.getX(i + 1), ri.getX(i + 2));
      }
      tuckedRoads = { geo: roads.geometry, was: ri };
      roads.geometry.setIndex(keep);
    }
  }
  const field = edge - 0.4;
  // The edge is a cliff over the open sea, not the world's mist: the fog bank goes while this stands, and the far fog
  // draws back so the sea reads below (both put back on dispose).
  const bank = ctx.fogBank?.group;
  const bankWas = bank?.visible ?? false;
  if (bank) bank.visible = false;
  const fog = ctx.scene.fog instanceof THREE.Fog ? ctx.scene.fog : null;
  const fogWas = fog ? { near: fog.near, far: fog.far } : null;
  if (fog) {
    fog.near *= 1.5;
    fog.far *= 2.2;
  }

  // The cliff: the grass ends in broken rock falling to the sea, chunks stepping down and out.
  const SEA_Y = TAIL_SEA_Y;
  // The cut face of the cliff, all the way down to the water: bands of rock stepping slightly out as they go down,
  // their rim broken by one shared noise so the bands meet without a seam.
  const cliffNoise = (a: number, y: number): number => 0.05 * Math.sin(a * 23 + y * 2.1) + 0.035 * Math.sin(a * 57 + 1.3) + 0.025 * Math.sin(a * 131 + y * 5.3);
  const bands = 3;
  for (let b = 0; b < bands; b++) {
    const y0 = 0.08 - (b * (0.08 - SEA_Y + 0.3)) / bands + 0.12;
    const y1 = 0.08 - ((b + 1) * (0.08 - SEA_Y + 0.3)) / bands;
    const g = new THREE.CylinderGeometry(1, 1, 1, low ? 64 : 112, 3, true);
    const pos = g.getAttribute('position');
    for (let i = 0; i < pos.count; i++) {
      const a = Math.atan2(pos.getZ(i), pos.getX(i));
      const t = pos.getY(i) + 0.5;
      const y = y1 + (y0 - y1) * t;
      const r = (edge - 0.1 + (1 - (y - SEA_Y) / (0.08 - SEA_Y)) * 0.8) * (1 + cliffNoise(a, y));
      pos.setXYZ(i, Math.cos(a) * r, y, Math.sin(a) * r);
    }
    g.computeVertexNormals();
    body.add(g, pick(CLIFF, b / bands + 0.1), new THREE.Matrix4());
    g.dispose();
  }
  const cliffN = low ? 40 : 72;
  for (let i = 0; i < cliffN; i++) {
    const a = (i / cliffN) * TAU + rnd() * 0.04;
    for (let k = 0; k < 2; k++) {
      const r = edge - 0.15 + k * 0.45 + rnd() * 0.2;
      const y = -0.2 - k * 1.15 - rnd() * 0.3;
      const s = 0.8 + rnd() * 0.6 - k * 0.1;
      body.add(P.rock, pick(ROCK, rnd()), place(Math.cos(a) * r, y, Math.sin(a) * r, rnd() * TAU, new THREE.Vector3(s, 1.3 + rnd() * 0.5, s * 0.8), (rnd() - 0.5) * 0.4));
    }
  }

  // The tail's dorsal plates: a ridge of slate fins across the field, beyond the fight, both ways along the tail.
  const axis = herd ? Math.atan2(herd.pen.y - herd.start.y, herd.pen.x - herd.start.x) + Math.PI / 2 : camp ? (camp.axis / 1024) * TAU + Math.PI / 2 : rnd() * TAU;
  for (const dir of [1, -1]) {
    const ca = Math.cos(axis) * dir;
    const sa = Math.sin(axis) * dir;
    let n = 0;
    for (let d = 7.2 * WORLD_PER_HEX; d < field + 1.6; d += 0.95 + rnd() * 0.25) {
      const side = n++ % 2 ? 1 : -1;
      const x = ca * d - sa * side * 0.32;
      const z = sa * d + ca * side * 0.32;
      if (d < field && nearRoad(hexAt(x, z))) continue;
      const p = ground(x, z);
      const tall = 1.6 + rnd() * 1.0 + Math.min(1.2, (d - 7 * WORLD_PER_HEX) * 0.08);
      // A fin: a flat slab standing on edge, leaning out, its foot sunk in rubble.
      const m = place(p.x, p.y - 0.1, p.z, axis + (rnd() - 0.5) * 0.3, 1, side * (0.06 + rnd() * 0.08), (rnd() - 0.5) * 0.08);
      // Wide at the root, narrowing to a blunt point: a plate grown out of the titan's back.
      body.add(P.cone, pick(SLATE, rnd()), m, place(0, tall / 2, 0, 0, new THREE.Vector3(1.1, tall, 0.22)));
      body.add(P.slab, pick(SLATE, rnd()), m, place(0, 0.18, 0, 0, new THREE.Vector3(1.05, 0.4, 0.26)));
      body.add(P.box, '#4a535e', m, place(0, tall * 0.55, 0.095, 0, new THREE.Vector3(0.6, 0.04, 0.02)));
      for (let k = 0; k < 3; k++) body.add(P.rock, pick(ROCK, rnd()), place(p.x + (rnd() - 0.5) * 0.9, p.y, p.z + (rnd() - 0.5) * 0.9, rnd() * TAU, 0.18 + rnd() * 0.2));
    }
  }

  // Cairns and standing stones daubed with the clan's red, out on the grass off the roads and the drive.
  const keepClear = (h: Hex): boolean => {
    if (nearRoad(h)) return false;
    if (!herd) return true;
    const w = hexToWorld(h.q, h.r);
    // Not on the line the herd is driven along.
    const ax = herd.start.x, ay = herd.start.y, bx = herd.pen.x, by = herd.pen.y;
    const dx = bx - ax, dy = by - ay;
    const t = Math.max(0, Math.min(1, ((w.x - ax) * dx + (w.y - ay) * dy) / (dx * dx + dy * dy || 1)));
    return Math.hypot(w.x - (ax + dx * t), w.y - (ay + dy * t)) > 2600;
  };
  const spots: Hex[] = [];
  for (let r = 5; r <= Math.min(10, map.radius); r++) for (const h of hexRing(r)) if (keepClear(h)) spots.push(h);
  const take = (): THREE.Vector3 | null => {
    if (!spots.length) return null;
    const h = spots.splice(Math.floor(rnd() * spots.length), 1)[0] as Hex;
    const w = hexToWorld(h.q, h.r);
    const p = ctx.at(w.x + (rnd() - 0.5) * 500, w.y + (rnd() - 0.5) * 500, 0);
    return ground(p.x, p.z);
  };
  for (let i = 0; i < (low ? 5 : 8); i++) {
    const p = take();
    if (!p) break;
    let y = p.y;
    const n = 3 + Math.floor(rnd() * 3);
    for (let k = 0; k < n; k++) {
      const s = 0.42 - k * 0.06;
      body.add(P.rock, pick(CAIRN, rnd()), place(p.x + (rnd() - 0.5) * 0.05, y + s * 0.22, p.z + (rnd() - 0.5) * 0.05, rnd() * TAU, new THREE.Vector3(s, s * 0.5, s * 0.85)));
      y += s * 0.4;
    }
  }
  for (let i = 0; i < 3; i++) {
    const p = take();
    if (!p) break;
    const m = place(p.x, p.y, p.z, rnd() * TAU, 1, (rnd() - 0.5) * 0.12, (rnd() - 0.5) * 0.12);
    const tall = 1.0 + rnd() * 0.4;
    // A rough standing stone, faceted, a smaller one leaning at its foot.
    body.add(P.rock, pick(CAIRN, rnd()), m, place(0, tall / 2 - 0.05, 0, 0, new THREE.Vector3(0.5, tall, 0.34)));
    body.add(P.rock, pick(CAIRN, rnd()), m, place(0.3, 0.12, 0.1, 1, 0.3, 0.3));
    // The clan's three red strokes.
    for (let k = 0; k < 3; k++) body.add(P.box, CLAN, m, place(-0.08 + k * 0.08, tall * 0.55, 0.165, 0, new THREE.Vector3(0.035, 0.3, 0.01), 0, 0.15));
  }

  // The corral at the pen: rails in a ring, the gate open toward the field; hay, a trough, the clan's horn on a post.
  let gateTop: THREE.Vector3 | null = null;
  if (herd) {
    const c = ctx.at(herd.pen.x, herd.pen.y, 0);
    const R = herd.penR * K + 0.15;
    const toField = Math.atan2(-c.z, -c.x);
    body.add(P.pad, '#5e5042', place(c.x, topOf(c.x, c.z, R) + 0.03, c.z, 0, new THREE.Vector3(R * 2, 0.6, R * 2)));
    const N = Math.max(14, Math.round((TAU * R) / 0.55));
    const gap = (a: number): boolean => {
      let d = Math.abs(a - toField) % TAU;
      d = Math.min(d, TAU - d);
      return d < 0.32;
    };
    const ptAt = (a: number): THREE.Vector3 => ground(c.x + Math.cos(a) * R, c.z + Math.sin(a) * R);
    for (let k = 0; k < N; k++) {
      const a = (k / N) * TAU;
      const b = ((k + 1) / N) * TAU;
      if (gap(a) && gap(b)) continue;
      const p = ptAt(a);
      if (!gap(a)) body.add(P.cyl, pick(POST, rnd()), place(p.x, p.y + 0.3, p.z, rnd() * TAU, new THREE.Vector3(0.09, 0.6 + rnd() * 0.08, 0.09), (rnd() - 0.5) * 0.08));
      if (gap(a) || gap(b)) continue;
      const q = ptAt(b);
      const len = Math.hypot(q.x - p.x, q.z - p.z) + 0.06;
      const yaw = Math.atan2(q.x - p.x, q.z - p.z);
      for (const y of [0.24, 0.46]) body.add(P.box, pick(RAIL, rnd()), place((p.x + q.x) / 2, (p.y + q.y) / 2 + y + (rnd() - 0.5) * 0.03, (p.z + q.z) / 2, yaw, new THREE.Vector3(0.07, 0.07, len), (rnd() - 0.5) * 0.06));
    }
    // The gate's two tall posts, a rail across the top; the gate itself swung wide open outward.
    for (const s of [-1, 1]) {
      const a = toField + s * 0.34;
      const p = ptAt(a);
      body.add(P.cyl, pick(POST, rnd()), place(p.x, p.y + 0.48, p.z, 0, new THREE.Vector3(0.12, 0.96, 0.12)));
      if (s > 0) {
        gateTop = p.clone().setY(p.y + 1.02);
        const o = Math.cos(a), w = Math.sin(a);
        // Swung open: its rails out along the fence's tangent, pointing away from the field.
        const tx = -w, tz = o;
        for (const y of [0.22, 0.42]) body.add(P.box, pick(RAIL, rnd()), place(p.x + tx * 0.42 + o * 0.25, p.y + y, p.z + tz * 0.42 + w * 0.25, Math.atan2(tx + o * 0.6, tz + w * 0.6), new THREE.Vector3(0.06, 0.06, 0.85)));
        // The clan's horn and a long red streamer at the top of the post.
        for (let i = 0; i < 6; i++) {
          const t = i / 5;
          body.add(P.cyl, i === 5 ? '#3a3d44' : i % 2 ? '#d6cbb6' : '#c4b8a2', place(p.x + 0.12 + Math.sin(t * 1.3) * 0.16, p.y + 0.82 - t * 0.12 + t * t * 0.14, p.z, 0, new THREE.Vector3(0.05 + t * 0.06, 0.1, 0.05 + t * 0.06), 0, 0.9 - t * 1.1));
        }
        body.add(P.cone, CLAN, place(p.x, p.y + 1.02, p.z, 0, new THREE.Vector3(0.12, 0.16, 0.12), Math.PI));
        for (let i = 0; i < 4; i++) body.add(P.box, CLAN, place(p.x - 0.06 - i * 0.12, p.y + 0.94 - i * 0.05, p.z + 0.02, 0, new THREE.Vector3(0.12, 0.05, 0.015), 0, 0.2));
      }
    }
    // Hay heaped against the far side, a split-log trough with water.
    const far = toField + Math.PI;
    for (let i = 0; i < 7; i++) {
      const a = far + (i - 3) * 0.28;
      const p = ground(c.x + Math.cos(a) * (R - 0.4), c.z + Math.sin(a) * (R - 0.4));
      body.add(P.blob, pick(HAY, rnd()), place(p.x, p.y + 0.06, p.z, rnd() * TAU, new THREE.Vector3(0.5 + rnd() * 0.2, 0.22 + rnd() * 0.1, 0.4)));
    }
    const ta = far + 1.25;
    const tp = ground(c.x + Math.cos(ta) * (R - 0.5), c.z + Math.sin(ta) * (R - 0.5));
    body.add(P.box, '#4e3e30', place(tp.x, tp.y + 0.1, tp.z, ta, new THREE.Vector3(0.8, 0.18, 0.26)));
    body.add(P.box, '#4a7088', place(tp.x, tp.y + 0.2, tp.z, ta, new THREE.Vector3(0.7, 0.02, 0.17)));
  }
  // Where the herd starts: trodden ground, a scatter of droppings of stone and a herders' windbreak of hides.
  if (herd) {
    const c = ctx.at(herd.start.x, herd.start.y, 0);
    body.add(P.pad, '#5a5244', place(c.x, topOf(c.x, c.z, 1.6) + 0.02, c.z, 0, new THREE.Vector3(3.2, 0.6, 3.2)));
    const away = Math.atan2(c.z, c.x);
    const wb = ground(c.x + Math.cos(away) * 1.9, c.z + Math.sin(away) * 1.9);
    const m = place(wb.x, wb.y, wb.z, -away + Math.PI / 2);
    for (const s of [-1, 1]) body.add(P.cyl, pick(POST, rnd()), m, place(s * 0.5, 0.45, 0, 0, new THREE.Vector3(0.07, 0.9, 0.07)));
    body.add(P.box, '#6e5e4e', m, place(0, 0.5, 0, 0, new THREE.Vector3(1.05, 0.62, 0.04), 0.18));
    body.add(P.box, '#5a4a3c', m, place(0.1, 0.35, 0.02, 0, new THREE.Vector3(0.6, 0.3, 0.03), 0.22));
  }

  // The clan's summer camp round the node (El Cuerno 2): hide tents, a fire pit, racks of drying meat, the clan's
  // banner; and the mooring posts they tie themselves to when the tail swings.
  let fire: THREE.Vector3 | null = null;
  if (camp) {
    const held = [...camp.posts, ...camp.barrels];
    const free = (w: { x: number; y: number }): boolean => held.every((p) => Math.hypot(p.x - w.x, p.y - w.y) > 1500);
    const near: Hex[] = [];
    for (const r of [2, 3]) for (const h of hexRing(r)) if (!nearRoad(h, 0) && free(hexToWorld(h.q, h.r))) near.push(h);
    const spot = (): THREE.Vector3 | null => {
      if (!near.length) return null;
      const h = near.splice(Math.floor(rnd() * near.length), 1)[0] as Hex;
      // Its neighbours aren't taken by the next piece.
      for (let i = near.length - 1; i >= 0; i--) if (hexDist(near[i] as Hex, h) < 2) near.splice(i, 1);
      const w = hexToWorld(h.q, h.r);
      const p = ctx.at(w.x + (rnd() - 0.5) * 300, w.y + (rnd() - 0.5) * 300, 0);
      return ground(p.x, p.z);
    };
    // The fire first, the rest round it.
    fire = spot();
    if (fire) {
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * TAU;
        body.add(P.rock, pick(CAIRN, rnd()), place(fire.x + Math.cos(a) * 0.32, fire.y + 0.04, fire.z + Math.sin(a) * 0.32, rnd() * TAU, new THREE.Vector3(0.18, 0.12, 0.15)));
      }
      body.add(P.disc, '#2a2522', place(fire.x, fire.y + 0.02, fire.z, 0, new THREE.Vector3(0.55, 1, 0.55)));
      for (let i = 0; i < 3; i++) body.add(P.cyl, '#4a3a2e', place(fire.x, fire.y + 0.1, fire.z, (i / 3) * Math.PI, new THREE.Vector3(0.06, 0.5, 0.06), Math.PI / 2 - 0.2));
      body.add(P.blob, '#c4562e', place(fire.x, fire.y + 0.1, fire.z, 0, new THREE.Vector3(0.22, 0.1, 0.22)));
    }
    for (let i = 0; i < (low ? 3 : 4); i++) {
      const p = spot();
      if (!p) break;
      // A hide tent: two slanted sheets on a ridge pole, the door toward the fire, the clan's red stripe on it.
      const yaw = fire ? Math.atan2(fire.x - p.x, fire.z - p.z) : rnd() * TAU;
      const m = place(p.x, p.y, p.z, yaw);
      const hide = pick(HIDE, rnd());
      for (const sd of [-1, 1]) body.add(P.box, hide, m, place(sd * 0.3, 0.38, 0, 0, new THREE.Vector3(0.8, 0.04, 1.05), 0, sd * 0.85));
      body.add(P.cyl, pick(POST, rnd()), m, place(0, 0.68, 0, 0, new THREE.Vector3(0.05, 1.15, 0.05), Math.PI / 2));
      for (const sd of [-1, 1]) body.add(P.box, CLAN, m, place(sd * 0.3, 0.4, 0.53, 0, new THREE.Vector3(0.6, 0.06, 0.01), 0, sd * 0.85));
      // Its back end closed with a hide; the front open, a dark mouth.
      body.add(P.cone, hide, m, place(0, 0.32, -0.5, Math.PI / 4, new THREE.Vector3(0.95, 0.7, 0.04)));
      body.add(P.cone, '#2e2622', m, place(0, 0.26, 0.5, Math.PI / 4, new THREE.Vector3(0.5, 0.5, 0.02)));
    }
    for (let i = 0; i < 2; i++) {
      const p = spot();
      if (!p) break;
      // A drying rack: two posts, a bar, strips of meat and hide hanging in the wind.
      const m = place(p.x, p.y, p.z, rnd() * TAU);
      for (const sd of [-1, 1]) body.add(P.cyl, pick(POST, rnd()), m, place(sd * 0.45, 0.42, 0, 0, new THREE.Vector3(0.06, 0.84, 0.06)));
      body.add(P.cyl, pick(POST, rnd()), m, place(0, 0.82, 0, 0, new THREE.Vector3(0.04, 1.0, 0.04), 0, Math.PI / 2));
      for (let k = 0; k < 5; k++) body.add(P.box, k % 2 ? '#7a3a30' : '#8a6e58', m, place(-0.32 + k * 0.16, 0.62, 0, 0, new THREE.Vector3(0.08, 0.36 + rnd() * 0.1, 0.02), 0.1 * (rnd() - 0.5)));
    }
    {
      const p = spot();
      if (p) {
        // The clan's banner: a tall pole, the horn at the top, a long red banner blown out on the wind.
        body.add(P.cyl, pick(POST, rnd()), place(p.x, p.y + 1.15, p.z, 0, new THREE.Vector3(0.07, 2.3, 0.07)));
        for (let k = 0; k < 5; k++) body.add(P.box, CLAN, place(p.x + 0.22 + k * 0.2, p.y + 2.05 - k * 0.07 + (k % 2) * 0.03, p.z, 0, new THREE.Vector3(0.22, 0.5 - k * 0.06, 0.02), 0, -0.1 - k * 0.04));
        for (let i = 0; i < 5; i++) {
          const t = i / 4;
          body.add(P.cyl, i % 2 ? '#d6cbb6' : '#c4b8a2', place(p.x - 0.05 + Math.sin(t * 1.3) * 0.18, p.y + 2.35 + t * 0.1 - t * t * 0.08, p.z, 0, new THREE.Vector3(0.06 + t * 0.07, 0.11, 0.06 + t * 0.07), 0, 0.9 - t * 1.2));
        }
      }
    }
    // The mooring posts: thick and dark, carved in bands, a rope coiled at the foot and two run out to stakes.
    for (const w of camp.posts) {
      const c = ctx.at(w.x, w.y, 0);
      const p = ground(c.x, c.z);
      const yaw = rnd() * TAU;
      const m = place(p.x, p.y, p.z, yaw, 1, (rnd() - 0.5) * 0.06, (rnd() - 0.5) * 0.06);
      body.add(P.cyl, '#4e3e32', m, place(0, 0.7, 0, 0, new THREE.Vector3(0.32, 1.4, 0.32)));
      for (const y of [0.35, 0.8, 1.2]) body.add(P.cyl, '#3a2e26', m, place(0, y, 0, 0, new THREE.Vector3(0.35, 0.06, 0.35)));
      body.add(P.cyl, '#3a2e26', m, place(0, 1.46, 0, 0, new THREE.Vector3(0.24, 0.14, 0.24)));
      for (let k = 0; k < 3; k++) body.add(P.box, CLAN, m, place(-0.06 + k * 0.06, 0.58, 0.165, 0, new THREE.Vector3(0.035, 0.26, 0.01), 0, 0.15));
      for (let k = 0; k < 2; k++) body.add(P.coil, '#a29478', m, place(0.42, 0.04 + k * 0.06, 0.1, 0, new THREE.Vector3(0.4 - k * 0.06, 0.6, 0.4 - k * 0.06)));
      body.add(P.coil, '#a29478', m, place(0, 1.0, 0, 0, new THREE.Vector3(0.36, 0.5, 0.36)));
      for (const sd of [-1, 1]) {
        // From the post's middle down to a stake in the ground.
        const from = new THREE.Vector3(0, 0.95, 0);
        const to = new THREE.Vector3(sd * 0.95, 0.12, -0.55);
        const d = to.clone().sub(from);
        const rope = new THREE.Matrix4().compose(from.clone().add(to).multiplyScalar(0.5), new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.clone().normalize()), new THREE.Vector3(0.025, d.length(), 0.025));
        body.add(P.cyl, '#a29478', m, rope);
        body.add(P.cyl, pick(POST, rnd()), m, place(to.x, 0.08, to.z, 0, new THREE.Vector3(0.05, 0.24, 0.05), 0.3 * sd));
      }
    }
  }

  // The clan's graveyard (El Cuerno 4).
  const flowers: THREE.Vector3[] = [];
  if (look.grave) graveyard(ctx, map, body, rnd, ground, nearRoad, flowers);

  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, flatShading: true });
  const mesh = body.build(mat, 'tail-body');
  if (mesh) {
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  // The grass, bending in the wind: tall tufts across the open ground outside the clearing.
  const time = { value: 0 };
  const grassMat = new THREE.MeshStandardMaterial({ roughness: 1, flatShading: true });
  swayGrass(grassMat, time);
  const blade = new THREE.ConeGeometry(0.08, 1, 3).translate(0, 0.5, 0);
  const nGrass = low ? 320 : 820;
  const grass = new THREE.InstancedMesh(blade, grassMat, nGrass);
  grass.name = 'tail-grass';
  const gm = new THREE.Matrix4();
  const gq = new THREE.Quaternion();
  const ge = new THREE.Euler();
  const gc = new THREE.Color();
  // At night in the graveyard the grass is dry and pale, like ash under the moon.
  const GRASS = night ? ['#6c6c5e', '#76745f', '#62625a', '#7e7c68', '#6a6a5c'] : ['#86a262', '#94ac6a', '#7a9658', '#a0b474', '#8aa060'];
  let gi = 0;
  for (let tries = 0; gi < nGrass && tries < nGrass * 6; tries++) {
    const a = rnd() * TAU;
    const r = 4.6 * WORLD_PER_HEX + Math.sqrt(rnd()) * (field - 4.6 * WORLD_PER_HEX);
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    if (nearRoad(hexAt(x, z), 0)) continue;
    const y = ctx.ground(x, z);
    for (let k = 0; k < 3 && gi < nGrass; k++) {
      ge.set((rnd() - 0.5) * 0.4, rnd() * TAU, (rnd() - 0.5) * 0.4);
      gq.setFromEuler(ge);
      const h = 0.22 + rnd() * 0.3;
      gm.compose(new THREE.Vector3(x + (rnd() - 0.5) * 0.25, y - 0.02, z + (rnd() - 0.5) * 0.25), gq, new THREE.Vector3(0.8 + rnd() * 0.5, h, 0.8 + rnd() * 0.5));
      grass.setMatrixAt(gi, gm);
      grass.setColorAt(gi, gc.set(pick(GRASS, rnd())).multiplyScalar(0.9 + rnd() * 0.2));
      gi++;
    }
  }
  grass.count = gi;
  grass.instanceMatrix.needsUpdate = true;
  if (grass.instanceColor) grass.instanceColor.needsUpdate = true;
  grass.computeBoundingSphere();
  group.add(grass);

  // The sea far below, from the foot of the cliff out to the haze: one flat ring, deep near the rock, pale far off.
  const seaGeo = new THREE.RingGeometry(edge - 0.6, edge * 7, 64, 4).rotateX(-Math.PI / 2);
  const seaCol: number[] = [];
  const sp = seaGeo.getAttribute('position');
  for (let i = 0; i < sp.count; i++) {
    const r = Math.hypot(sp.getX(i), sp.getZ(i));
    gc.copy(night ? SEA_NEAR_N : SEA_NEAR).lerp(night ? SEA_FAR_N : SEA_FAR, Math.min(1, (r - edge) / (edge * 3)));
    seaCol.push(gc.r, gc.g, gc.b);
  }
  seaGeo.setAttribute('color', new THREE.Float32BufferAttribute(seaCol, 3));
  const seaMat = new THREE.MeshBasicMaterial({ vertexColors: true });
  const sea = new THREE.Mesh(seaGeo, seaMat);
  sea.name = 'tail-sea';
  sea.position.y = SEA_Y;
  group.add(sea);

  // Gulls wheeling over the cliff: a pale V each.
  const gullGeo = new THREE.BufferGeometry();
  gullGeo.setAttribute('position', new THREE.Float32BufferAttribute([-0.22, 0.05, -0.02, 0, 0, 0.04, 0, 0, -0.04, 0.22, 0.05, -0.02, 0, 0, -0.04, 0, 0, 0.04], 3));
  gullGeo.computeVertexNormals();
  const gullMat = new THREE.MeshBasicMaterial({ color: '#eef2f6', side: THREE.DoubleSide });
  const gullN = night ? 0 : low ? 3 : 6;
  const gulls = new THREE.InstancedMesh(gullGeo, gullMat, gullN);
  gulls.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  gulls.frustumCulled = false;
  group.add(gulls);
  const gullPath = Array.from({ length: gullN }, () => ({ a: rnd() * TAU, r: edge * (0.9 + rnd() * 0.5), h: 2.5 + rnd() * 2.5, w: (0.08 + rnd() * 0.08) * (rnd() < 0.5 ? 1 : -1), ph: rnd() * TAU }));

  // Whitecaps on the sea, seeds blown across the field, a soft wash of daylight: one glow layer.
  const glow = new GlowLayer(night ? (low ? 120 : 270) : low ? 90 : 210);
  group.add(glow.mesh);
  const caps = Array.from({ length: low ? 18 : 40 }, () => {
    const a = rnd() * TAU;
    const r = edge + 0.8 + rnd() * edge * 1.6;
    return { x: Math.cos(a) * r, z: Math.sin(a) * r, ph: rnd() * TAU, s: 0.6 + rnd() * 1.2 };
  });
  const surf = Array.from({ length: low ? 24 : 56 }, (_, i) => {
    const a = (i / (low ? 24 : 56)) * TAU + rnd() * 0.08;
    const r = edge + 0.75 + rnd() * 0.35;
    return { x: Math.cos(a) * r, z: Math.sin(a) * r, ph: rnd() * TAU, s: 0.7 + rnd() * 0.6 };
  });
  const seeds = Array.from({ length: night ? 0 : low ? 16 : 40 }, () => ({ x: (rnd() - 0.5) * field * 2, z: (rnd() - 0.5) * field * 2, h: 0.4 + rnd() * 1.6, ph: rnd() * TAU, v: 0.6 + rnd() * 0.6 }));

  // At night: mist low among the graves, moonlight falling on them, a few wisps drifting over the stones.
  const mists = night
    ? Array.from({ length: low ? 16 : 40 }, () => {
        const a = rnd() * TAU;
        const r = (2.5 + rnd() * 6.5) * WORLD_PER_HEX;
        return { p: new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r), size: 2.0 + rnd() * 2.2, ph: rnd() * TAU, drift: 0.1 + rnd() * 0.2 };
      })
    : [];
  for (const m of mists) m.p.y = ctx.ground(m.p.x, m.p.z) + 0.3;
  const shafts = night
    ? Array.from({ length: low ? 3 : 6 }, () => {
        const a = rnd() * TAU;
        const r = (2 + rnd() * 5) * WORLD_PER_HEX;
        return { p: new THREE.Vector3(Math.cos(a) * r, 0.05, Math.sin(a) * r), ph: rnd() * TAU };
      })
    : [];
  const wisps = night ? Array.from({ length: low ? 5 : 12 }, () => ({ a: rnd() * TAU, r: (3 + rnd() * 5) * WORLD_PER_HEX, w: (0.03 + rnd() * 0.05) * (rnd() < 0.5 ? 1 : -1), ph: rnd() * TAU })) : [];

  const sky = new SkyLook(
    ctx,
    night
      ? {
          hemi: ['#56668a', '#141a24', 0.62],
          key: ['#b4c6ec', 0.58],
          rim: ['#8aa4d8', 1.1],
          background: '#0b1220',
          fog: '#121b2a',
          mist: { low: '#18202e', mid: '#121925', far: '#0b1018', alpha: 1.05 },
        }
      : {
          hemi: ['#d4e4f6', '#4e5c4a', 1.12],
          key: ['#f6f8ff', 1.08],
          rim: ['#a8cbff', 0.95],
          background: '#8eafcb',
          fog: '#a6c0d6',
          mist: { low: '#a9c0d4', mid: '#97b2c8', far: '#8eafcb', alpha: 0.24 },
        },
  );

  const gm2 = new THREE.Matrix4();
  const gq2 = new THREE.Quaternion();
  const ge2 = new THREE.Euler();
  const gv = new THREE.Vector3();
  const gs = new THREE.Vector3();
  return {
    group,
    frame: (t: number) => {
      sky.pulse({});
      time.value = t;
      for (let i = 0; i < gullN; i++) {
        const g = gullPath[i] as (typeof gullPath)[number];
        const a = g.a + t * g.w;
        const flap = Math.sin(t * 5 + g.ph) * 0.35;
        ge2.set(0, -a + (g.w > 0 ? 0 : Math.PI), flap * 0.3 + (g.w > 0 ? -0.25 : 0.25));
        gq2.setFromEuler(ge2);
        gs.set(1.4, 1 + flap, 1.4);
        gm2.compose(gv.set(Math.cos(a) * g.r, g.h + Math.sin(t * 0.6 + g.ph) * 0.3, Math.sin(a) * g.r), gq2, gs);
        gulls.setMatrixAt(i, gm2);
      }
      gulls.instanceMatrix.needsUpdate = true;
      glow.begin();
      const dim = night ? 0.45 : 1;
      for (const c of caps) glow.add(c.x, SEA_Y + 0.05, c.z, FOAM, c.s, Math.max(0, Math.sin(t * 0.8 + c.ph)) * 0.22 * dim);
      for (const m of mists) glow.add(m.p.x + Math.sin(t * m.drift + m.ph) * 0.4, m.p.y, m.p.z + Math.cos(t * m.drift * 0.7 + m.ph) * 0.3, MIST, m.size, 0.05 + 0.02 * Math.sin(t * 0.5 + m.ph));
      for (const m of shafts) glow.add(m.p.x, ctx.ground(m.p.x, m.p.z) + 0.05, m.p.z, MOON, 2.6, 0.06 + 0.02 * Math.sin(t * 0.3 + m.ph));
      for (const w of wisps) {
        const a = w.a + t * w.w;
        const x = Math.cos(a) * w.r;
        const z = Math.sin(a) * w.r;
        glow.add(x, 0.55 + Math.sin(t * 0.9 + w.ph) * 0.2 + ctx.ground(x, z), z, WISP, 0.32, 0.35 + 0.2 * Math.sin(t * 2.1 + w.ph));
      }
      for (const f of flowers) glow.add(f.x, f.y + 0.1, f.z, BLOOM_GLOW, 0.5, 0.18 + 0.05 * Math.sin(t * 0.8 + f.x));
      // Seeds on the wind, drifting with it across the field and round again.
      const span = field * 2;
      const wrap = (v: number): number => ((((v + field) % span) + span) % span) - field;
      for (const s of seeds) {
        const x = wrap(s.x + t * s.v * 0.9);
        const z = wrap(s.z + t * s.v * 0.35) + Math.sin(t * 0.9 + s.ph) * 0.4;
        glow.add(x, s.h + Math.sin(t * 1.7 + s.ph) * 0.15, z, SEED, 0.16, 0.5);
      }
      if (gateTop) glow.add(gateTop.x, gateTop.y, gateTop.z, SUN, 1.4, 0.1);
      // The surf breaking white at the cliff's foot.
      for (const f of surf) glow.add(f.x, SEA_Y + 0.08, f.z, FOAM, f.s, (0.18 + 0.14 * Math.max(0, Math.sin(t * 1.3 + f.ph))) * dim);
      if (fire) {
        const k = 0.75 + 0.25 * Math.sin(t * 9) * Math.sin(t * 5.3 + 1);
        glow.add(fire.x, fire.y + 0.3, fire.z, EMBER, 1.6 * k, 0.45);
        glow.add(fire.x, fire.y + 0.55, fire.z, EMBER, 0.6 * k, 0.6);
      }
      glow.end();
    },
    dispose: () => {
      sky.restore();
      if (bank) bank.visible = bankWas;
      if (fog && fogWas) {
        fog.near = fogWas.near;
        fog.far = fogWas.far;
      }
      for (const o of hidden) o.visible = true;
      if (turf) turf.mat.color.copy(turf.was);
      if (cut) cut.geo.setIndex(cut.was);
      if (tuckedRoads) tuckedRoads.geo.setIndex(tuckedRoads.was);
      for (const t of tucked) {
        t.mesh.setMatrixAt(t.i, t.m);
        t.mesh.instanceMatrix.needsUpdate = true;
      }
      mesh?.geometry.dispose();
      mat.dispose();
      blade.dispose();
      grassMat.dispose();
      grass.dispose();
      seaGeo.dispose();
      seaMat.dispose();
      gullGeo.dispose();
      gullMat.dispose();
      gulls.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};

/**
 * The clan of the Horn's graveyard over the field (El Cuerno 4): off the roads, thickest a few hexes out from the camp.
 * Grave stones with the horn carved on them over low mounds, long cairns over the older dead, the family graves kerbed
 * in stone (the nearest one the sisters' mother's: red wool on her stone, pale flowers left on it, `flowers` gets where),
 * clan poles with a horn and red wool, and two great horned stones either side of a road where it comes in.
 */
const graveyard = (
  ctx: OmenCtx,
  map: MapDef,
  body: Kit,
  rnd: () => number,
  ground: (x: number, z: number) => THREE.Vector3,
  nearRoad: (h: Hex, d?: number) => boolean,
  flowers: THREE.Vector3[],
): void => {
  const low = ctx.low;
  const outer = Math.min(9, map.radius - 2);
  const spots: Hex[] = [];
  for (let r = 3; r <= outer; r++) for (const h of hexRing(r)) if (!nearRoad(h, 0)) spots.push(h);
  const at = (h: Hex, j = 500): THREE.Vector3 => {
    const w = hexToWorld(h.q, h.r);
    const p = ctx.at(w.x + (rnd() - 0.5) * j, w.y + (rnd() - 0.5) * j, 0);
    return ground(p.x, p.z);
  };
  const take = (f: (h: Hex) => boolean): Hex | null => {
    const ok = spots.filter(f);
    if (!ok.length) return null;
    const h = ok[Math.floor(rnd() * ok.length)] as Hex;
    spots.splice(spots.indexOf(h), 1);
    return h;
  };
  /** A grave stone: a slab leaning a little, the horn carved on its face and the clan's mark under it; a mound before it. */
  const stone = (p: THREE.Vector3, yaw: number, big = 1): THREE.Matrix4 => {
    const m = place(p.x, p.y, p.z, yaw, big, (rnd() - 0.5) * 0.14, (rnd() - 0.5) * 0.12);
    const tall = (0.55 + rnd() * 0.25) * (big > 1 ? 1.25 : 1);
    const c = pick(GRAVE, rnd());
    body.add(P.box, c, m, place(0, tall / 2 - 0.04, 0, 0, new THREE.Vector3(0.44, tall, 0.11)));
    // A rounded top.
    body.add(P.cyl, c, m, place(0, tall - 0.04, 0, 0, new THREE.Vector3(0.44, 0.11, 0.22), Math.PI / 2));
    body.add(P.horn, pick(GRAVE_D, rnd()), m, place(0, tall * 0.62, 0.058, 0, new THREE.Vector3(0.24, 0.24, 0.25), 0, Math.PI * 0.08));
    body.add(P.box, pick(GRAVE_D, rnd()), m, place(0, tall * 0.3, 0.057, 0, new THREE.Vector3(0.16, 0.025, 0.01)));
    body.add(P.blob, pick(MOUND, rnd()), m, place(0, 0.02, -0.6, 0, new THREE.Vector3(0.55, 0.16, 0.95)));
    return m;
  };
  /** A long cairn over an older grave: stones heaped in a low ridge. */
  const cairn = (p: THREE.Vector3, yaw: number): void => {
    const m = place(p.x, p.y, p.z, yaw);
    for (let i = 0; i < 9; i++) {
      const z = -0.5 + (i % 5) * 0.25;
      const top = i >= 5;
      body.add(P.rock, pick(CAIRN, rnd()), m, place((rnd() - 0.5) * 0.25, top ? 0.17 : 0.07, top ? z + 0.12 : z, rnd() * TAU, new THREE.Vector3(0.26, 0.17, 0.22)));
    }
  };
  /** A clan pole: the horn on top, red wool tied on, the wool's ends hanging. */
  const pole = (p: THREE.Vector3): void => {
    const yaw = rnd() * TAU;
    const m = place(p.x, p.y, p.z, yaw, 1, (rnd() - 0.5) * 0.08);
    body.add(P.cyl, pick(POST, rnd()), m, place(0, 0.85, 0, 0, new THREE.Vector3(0.07, 1.7, 0.07)));
    for (let i = 0; i < 5; i++) {
      const t = i / 4;
      body.add(P.cyl, i % 2 ? '#d6cbb6' : '#c4b8a2', m, place(0.04 + Math.sin(t * 1.3) * 0.16, 1.72 + t * 0.12 - t * t * 0.1, 0, 0, new THREE.Vector3(0.05 + t * 0.06, 0.1, 0.05 + t * 0.06), 0, 0.9 - t * 1.2));
    }
    for (const y of [1.25, 1.35]) body.add(P.cyl, CLAN, m, place(0, y, 0, 0, new THREE.Vector3(0.1, 0.05, 0.1)));
    for (let k = 0; k < 3; k++) body.add(P.box, CLAN, m, place(0.05 + k * 0.025, 1.0 - k * 0.04, 0.04, 0, new THREE.Vector3(0.025, 0.42 - k * 0.08, 0.01), 0, 0.08 + k * 0.05));
  };
  /** A family grave: kerbed in stone round a long mound, a big stone at its head. */
  const family = (p: THREE.Vector3, yaw: number): THREE.Matrix4 => {
    const m = place(p.x, p.y, p.z, yaw);
    // A low kerb, dark and sunk: from above it must read as a stone border, not a frame standing up.
    for (const sx of [-1, 1]) body.add(P.box, pick(GRAVE_D, rnd()), m, place(sx * 0.45, 0.02, -0.6, 0, new THREE.Vector3(0.12, 0.08, 1.3)));
    body.add(P.box, pick(GRAVE_D, rnd()), m, place(0, 0.02, -1.25, 0, new THREE.Vector3(1.02, 0.08, 0.12)));
    body.add(P.blob, pick(MOUND, rnd()), m, place(0, 0.05, -0.62, 0, new THREE.Vector3(0.78, 0.22, 1.2)));
    stone(p, yaw, 1.35);
    return m;
  };

  // The way in: two great horned stones either side of the first road, where it reaches the graves.
  const road = map.sectors[0]?.path ?? [];
  const gate = road.find((h) => hexDist(h, { q: 0, r: 0 }) === Math.min(6, outer));
  const nextIn = road.find((h) => gate && hexDist(h, { q: 0, r: 0 }) === hexDist(gate, { q: 0, r: 0 }) - 1);
  if (gate && nextIn) {
    const g = hexToWorld(gate.q, gate.r);
    const n = hexToWorld(nextIn.q, nextIn.r);
    const c = ctx.at(g.x, g.y, 0);
    const d = ctx.at(n.x, n.y, 0);
    const along = Math.atan2(d.x - c.x, d.z - c.z);
    const side = new THREE.Vector3(Math.cos(along), 0, -Math.sin(along));
    for (const sd of [-1, 1]) {
      const q = ground(c.x + side.x * sd * 0.95, c.z + side.z * sd * 0.95);
      const m = place(q.x, q.y, q.z, along, 1.05);
      body.add(P.rock, pick(GRAVE, rnd()), m, place(0, 0.75, 0, 0, new THREE.Vector3(0.55, 1.6, 0.4)));
      // Its horn: curving up and outward from the top.
      for (let i = 0; i < 6; i++) {
        const t = i / 5;
        body.add(P.cyl, i === 5 ? '#3a3d44' : i % 2 ? '#d6cbb6' : '#c4b8a2', m, place(sd * (0.08 + Math.sin(t * 1.4) * 0.3), 1.45 + t * 0.35 - t * t * 0.12, 0, 0, new THREE.Vector3(0.11 - t * 0.07, 0.16, 0.11 - t * 0.07), 0, -sd * (0.4 - t * 1.3)));
      }
      for (let k = 0; k < 3; k++) body.add(P.box, CLAN, m, place(-0.08 + k * 0.08, 0.9, 0.2, 0, new THREE.Vector3(0.035, 0.32, 0.01), 0, 0.15));
      for (let k = 0; k < 4; k++) body.add(P.rock, pick(CAIRN, rnd()), place(q.x + (rnd() - 0.5) * 0.7, q.y, q.z + (rnd() - 0.5) * 0.7, rnd() * TAU, 0.16 + rnd() * 0.12));
      for (let i = spots.length - 1; i >= 0; i--) if (hexDist(spots[i] as Hex, gate) <= 1) spots.splice(i, 1);
    }
  }

  // The sisters' mother: the nearest family grave to the camp, off the roads; red wool on its stone, flowers on it.
  const mh = take((h) => hexDist(h, { q: 0, r: 0 }) <= 4 && !nearRoad(h, 1));
  if (mh) {
    const p = at(mh, 200);
    const m = family(p, (rnd() - 0.5) * 0.3);
    for (let k = 0; k < 4; k++) body.add(P.box, CLAN, m, place(-0.12 + k * 0.03, 0.52 - k * 0.02, 0.075, 0, new THREE.Vector3(0.026, 0.48 - k * 0.07, 0.012), 0, 0.12 + k * 0.08));
    body.add(P.cyl, CLAN, m, place(0, 0.62, 0, 0, new THREE.Vector3(0.5, 0.05, 0.16)));
    for (let k = 0; k < 7; k++) {
      const q = new THREE.Vector3((rnd() - 0.5) * 0.5, 0.2, -0.35 - rnd() * 0.6).applyMatrix4(m);
      body.add(P.blob, pick(BLOOM, rnd()), place(q.x, q.y, q.z, rnd() * TAU, new THREE.Vector3(0.09, 0.06, 0.09)));
      if (k % 3 === 0) flowers.push(q);
    }
    for (let i = spots.length - 1; i >= 0; i--) if (hexDist(spots[i] as Hex, mh) <= 1) spots.splice(i, 1);
  }
  for (let i = 0; i < (low ? 2 : 3); i++) {
    const h = take((x) => !nearRoad(x, 1));
    if (!h) break;
    const p = at(h, 300);
    family(p, (rnd() - 0.5) * 0.4);
    for (let k = spots.length - 1; k >= 0; k--) if (hexDist(spots[k] as Hex, h) <= 0) spots.splice(k, 1);
  }
  for (let i = 0; i < (low ? 3 : 5); i++) {
    const h = take(() => true);
    if (!h) break;
    pole(at(h, 400));
  }
  // The rows of the dead: stones all facing one way (toward the sea's open side, and the camera), older cairns among them.
  const graves = low ? 26 : 50;
  for (let i = 0; i < graves && spots.length; i++) {
    const h = take(() => true);
    if (!h) break;
    const n = rnd() < 0.4 ? 2 : 1;
    for (let k = 0; k < n; k++) {
      const p = at(h, 650);
      const yaw = (rnd() - 0.5) * 0.45;
      if (rnd() < 0.28) cairn(p, yaw);
      else stone(p, yaw);
    }
  }
};
